/**
 * Create / edit dialog for a note (contract §3.6). Fixed under the top bar so
 * it works wherever it was opened from; Escape closes, ⌘/ctrl+Enter saves.
 * A singleton: the app shell mounts it once, and should it ever be mounted
 * twice (two time axes on one page) only the first mounted instance renders.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteNote, useSaveNote } from "@/api/hooks";
import { isAddress } from "@/lib/jobs";
import { parseTags } from "./layout";
import { useNoteComposer } from "./composer";
import { invalidateFamilyCards } from "./invalidate";

// mounted instances in mount order; only the first one renders the dialog
const instances: symbol[] = [];
const listeners = new Set<() => void>();
function subscribeOwner(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyOwner(): void {
  for (const fn of listeners) fn();
}
function useSingleton(): boolean {
  const [id] = useState(() => Symbol("note-composer"));
  useEffect(() => {
    instances.push(id);
    notifyOwner();
    return () => {
      const i = instances.indexOf(id);
      if (i >= 0) instances.splice(i, 1);
      notifyOwner();
    };
  }, [id]);
  return useSyncExternalStore(
    subscribeOwner,
    () => instances[0] === id,
    () => false,
  );
}

function toLocalInput(t: number | undefined): string {
  if (t === undefined) return "";
  const d = new Date(t * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(s: string): number | undefined {
  if (!s) return undefined;
  const t = Math.floor(new Date(s).getTime() / 1000);
  return Number.isFinite(t) ? t : undefined;
}

export function NoteComposer() {
  const draft = useNoteComposer((s) => s.draft);
  const close = useNoteComposer((s) => s.close);
  const owner = useSingleton();
  if (!draft || !owner) return null;
  return <ComposerForm key={`${draft.id ?? "new"}:${draft.at_from}`} onClose={close} />;
}

function ComposerForm({ onClose }: { onClose: () => void }) {
  const draft = useNoteComposer((s) => s.draft)!;
  const qc = useQueryClient();
  const save = useSaveNote();
  const del = useDeleteNote();
  const [text, setText] = useState(draft.text ?? "");
  const [tags, setTags] = useState((draft.tags ?? []).join(", "));
  const [address, setAddress] = useState(draft.address ?? "");
  const [from, setFrom] = useState(toLocalInput(draft.at_from));
  const [to, setTo] = useState(toLocalInput(draft.at_to));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => {
    const atFrom = fromLocalInput(from);
    const atTo = fromLocalInput(to);
    if (atFrom === undefined) return setError("from is required");
    if (atTo !== undefined && atTo < atFrom) return setError("to must be after from");
    if (!text.trim()) return setError("text is required");
    const addr = address.trim().toLowerCase();
    if (addr && !isAddress(addr)) return setError("address must be 0x + 40 hex");
    setError(null);
    save.mutate(
      {
        id: draft.id,
        job: draft.job,
        at_from: atFrom,
        at_to: atTo !== undefined && atTo > atFrom ? atTo : undefined,
        address: addr || undefined,
        text: text.trim(),
        tags: parseTags(tags),
      },
      {
        onSuccess: () => {
          invalidateFamilyCards(qc, draft.job);
          onClose();
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  return (
    <div
      className="fixed top-[calc(var(--topbar-h)+8px)] left-1/2 z-40 w-[440px] max-w-[95vw] -translate-x-1/2 rounded-sm border border-border bg-bg-elev p-2 shadow-xl"
      role="dialog"
      aria-label={draft.id ? "edit note" : "new note"}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
      }}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
          {draft.id ? `edit note #${draft.id}` : "new note"}
        </span>
        <button type="button" className="text-fg-faint hover:text-fg" onClick={onClose} aria-label="close">
          ×
        </button>
      </div>
      <textarea
        className="ctl mb-1 h-16 w-full resize-y py-1 leading-snug"
        placeholder="what happened here?"
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mb-1 grid grid-cols-2 gap-1">
        <label className="flex flex-col text-2xs text-fg-faint">
          from
          <input
            className="ctl"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-2xs text-fg-faint">
          to (optional)
          <input className="ctl" type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="flex flex-col text-2xs text-fg-faint">
          address (optional)
          <input
            className="ctl num"
            placeholder="0x…"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </label>
        <label className="flex flex-col text-2xs text-fg-faint">
          tags
          <input className="ctl" placeholder="a, b" value={tags} onChange={(e) => setTags(e.target.value)} />
        </label>
      </div>
      {error && <div className="mb-1 text-[11px] text-danger">{error}</div>}
      <div className="flex items-center gap-1">
        <button type="button" className="ctl on" onClick={submit} disabled={save.isPending}>
          {save.isPending ? "saving…" : "save"}
        </button>
        <button type="button" className="ctl" onClick={onClose}>
          cancel
        </button>
        <span className="text-2xs text-fg-faint">⌘/ctrl+enter</span>
        <div className="flex-1" />
        {draft.id !== undefined && (
          <button
            type="button"
            className="ctl text-danger"
            disabled={del.isPending}
            onClick={() =>
              del.mutate(draft.id!, {
                onSuccess: () => {
                  invalidateFamilyCards(qc, draft.job);
                  onClose();
                },
                onError: (e) => setError(e.message),
              })
            }
          >
            delete
          </button>
        )}
      </div>
    </div>
  );
}
